import { useEffect, useRef, useState } from "react";
import ChevronDownIcon from "@heroicons/react/20/solid/ChevronDownIcon";
import ChevronUpIcon from "@heroicons/react/20/solid/ChevronUpIcon";
import XCircleIcon from "@heroicons/react/20/solid/XCircleIcon";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import { useNavigate } from "react-router-dom";

import useWindowDimensions from "@hsl/core/hooks/useWindowDimensions";
import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";
import { FundDataWithType } from "@hsl/fund-explorer/metaSchemas";

import { Translate } from "../../../hooks/useTranslation";
import Button from "../../Button";
import ShareIcon from "../../icons/Share";
import { FloatingBox } from "../FloatingBox";
import {
    SubscribeIcon,
    WatchIcon,
} from "../SubscriptionButtons/SubscriptionButtons";

export const SubscriptionSelectionBasket = () => {
    const { buttonClickEvent } = useYardActionEvents();
    const { width } = useWindowDimensions();
    const basket = useFCSubscriptionStore((state) => state.selectionBasket);
    const setStepData = useFCSubscriptionStore(
        (state) => state.setFlowStepData,
    );
    const navigate = useNavigate();
    const resetFlow = useFCSubscriptionStore((state) => state.resetFlow);
    const mode = useFCSubscriptionStore((state) => state.selectionBasketMode);
    const toggleBasketItem = useFCSubscriptionStore(
        (state) => state.toggleBasketItem,
    );
    const clearBasket = useFCSubscriptionStore((state) => state.clearBasket);
    const setSharingMode = useFCSubscriptionStore(
        (state) => state.setSharingMode,
    );
    const isRetrievingFunds = useFCSubscriptionStore(
        (state) => state.retrieveFunds,
    );
    const user = useFCSubscriptionStore((state) => state.user);
    const watchFunds = useFCSubscriptionStore((state) => state.watchFunds);
    const [collapsed, setCollapsed] = useState(width < 376);
    const ref = useRef<HTMLDivElement>(null!);

    console.log(width, width < 376);
    let icon = null;
    let helpText = "";
    let url = "/my-funds";

    switch (mode) {
        case "subscribe":
            icon = <SubscribeIcon active className="mt-1" />;
            helpText = "Add more funds by clicking on each bell icon";
            url += "/subscribe";
            break;
        case "watch":
            icon = <WatchIcon active className="mt-1" />;
            helpText = "Add more funds by clicking on each star icon";
            url += user ? "" : "/watch";
            break;
    }

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                ref.current &&
                !ref.current.contains(e.target as Node) &&
                window.innerWidth < 375
            ) {
                setCollapsed(true);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    async function handleBasketCTAClick() {
        resetFlow();
        if (!user || mode === "subscribe") {
            setStepData("select-funds", {
                complete: true,
                data: {
                    ...basket,
                    type: mode === "subscribe" ? "FULL" : "WATCHLIST",
                },
            });
        }
        if (user && mode === "watch") {
            await watchFunds(basket.funds);
        }
        navigate(url);
    }

    function handleShareClick() {
        resetFlow();
        setSharingMode(mode);
    }

    function handleRemove(item: FundDataWithType) {
        toggleBasketItem(item);
        buttonClickEvent({
            name: "Remove fund",
            position: "Subscription pop up",
            details: item.data.data.name,
            fund: {
                id: item.data.id,
                type: item.type,
                name: item.data.data.name,
            },
        });
    }

    return (
        <FloatingBox
            boxRef={ref}
            show={basket.funds.length > 0 && !isRetrievingFunds}
            onClose={() => setCollapsed((c) => !c)}
            closeButton={collapsed ? <ChevronUpIcon /> : <ChevronDownIcon />}
            contentContainerClassName={cx(
                "transition-all duration-500 linear",
                collapsed && "max-h-0 min-h-0 border-transparent",
            )}
            header={
                <>
                    <div
                        className="ml-1 flex flex-row"
                        onClick={() => setCollapsed(false)}
                    >
                        {icon}
                        <div className="ml-2">
                            <h2 className="text-lg">
                                <Translate>Select multiple funds</Translate>
                            </h2>
                            <p className="text-core-blue">
                                <span className="font-medium">
                                    {basket.funds.length}
                                </span>{" "}
                                <Translate>
                                    {basket.funds.length === 1
                                        ? "fund selected"
                                        : "funds selected"}
                                </Translate>
                            </p>
                            {basket.funds.length >= 10 ? (
                                <MaxFundsReached className="mt-1" />
                            ) : (
                                <p className="text-battleship-grey mt-px text-sm">
                                    <Translate>{helpText}</Translate>
                                </p>
                            )}
                        </div>
                    </div>
                </>
            }
            footer={
                <>
                    <Button
                        className="px-4"
                        variant="large"
                        as="link"
                        to={`/my-funds/${mode}/share`}
                        color="white"
                        icon={<ShareIcon className="text-white" />}
                        stripSearchParams={["step"]}
                        onClick={handleShareClick}
                    >
                        <Translate>Share</Translate>
                    </Button>
                    <Button
                        variant="large"
                        as="link"
                        to={url}
                        onClick={handleBasketCTAClick}
                        stripSearchParams={["step"]}
                    >
                        <Translate>
                            {mode === "subscribe"
                                ? "Subscribe"
                                : "Add to watchlist"}
                        </Translate>
                    </Button>
                </>
            }
        >
            <button
                className="text-core-blue my-1 text-sm underline"
                onClick={() => {
                    clearBasket();
                }}
            >
                <Translate>Clear all</Translate>
            </button>
            {basket.funds.map((item) => (
                <div
                    key={item.data.id}
                    className="border-white-smoke my-1 flex justify-between border-b py-1"
                    aria-hidden={collapsed}
                >
                    <p>{item.data.data.name}</p>
                    <button
                        aria-roledescription={`Remove ${item.data.data.name} from selection`}
                        className="-mr-1 -mt-px ml-2 inline h-max shrink-0 p-1"
                        onClick={() => handleRemove(item)}
                    >
                        <XMarkIcon width={18} className="text-taupe-gray" />
                    </button>
                </div>
            ))}
        </FloatingBox>
    );
};

interface Props {
    className?: string;
}

const MaxFundsReached = ({ className }: Props) => {
    return (
        <div
            className={cx(
                "inline-flex items-start rounded-sm bg-[#FADBDA] py-1 pl-2 pr-3 text-sm",
                className,
            )}
        >
            <XCircleIcon
                width={16}
                className="text-core-red mr-2 mt-0.5 inline shrink-0"
            />
            <p>
                <Translate>A maximum of 10 funds can be selected</Translate>
            </p>
        </div>
    );
};
